import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { AdminOverrideBadgeComponent } from '@features/shopping/admin-override/admin-override-badge/admin-override-badge.component';
import { AddOnFeaturesParam, BusinessPlansParam } from '@models/customize-service.model';
import { createShoppingObject, stringOnlyHTML } from '@models/functions.model';
import { ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import { CART_ITEM_TYPE_FEATURE, FeatureType, ManageAddOnFeatures } from '@models/shopping.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { CustomizeService } from '@services/customize-service/customize.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { BaseBadgeComponent } from '@shared-components/badge/base-badge/base-badge.component';
import { ViewDetailsComponent } from '@shared-components/view-details/view-details.component';
import { Observable, Subscription, takeUntil } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-add-on-features',
  templateUrl: './add-on-features.component.html',
  imports: [
    AdminOverrideBadgeComponent,
    AsButtonDirective,
    BaseBadgeComponent,
    CommonModule,
    DataLoaderDirective,
    StopClickPropagationDirective,
    TranslationPipe
  ]
})
export class AddOnFeaturesComponent implements OnInit, OnDestroy {
  shopping_cart: ShoppingCartData;
  feature_state: AddOnFeaturesParam;
  no_features: boolean;
  features: FeatureType[] = [];
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_svc: ActivityManagerService,
    private customize_svc: CustomizeService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.customize_svc.state['plans_param']?.subscribe(() => (this.features = []))
    );

    this.subscriptions.add(
      this.customize_svc.state['features_param']?.subscribe((res: AddOnFeaturesParam) => {
        this.shopping_cart = this.customize_svc.getCart();
        this.feature_state = res!;
        this.getAddOnFeatures();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFeature(feature: FeatureType | ShoppingCartItem): FeatureType | undefined {
    return this.features.find((i: FeatureType) => i.id == feature.id);
  }

  get cart_features(): ShoppingCartItem[] | undefined {
    return (
      this.shopping_cart &&
      this.shopping_cart.shopping_cart_items.filter(
        (i: ShoppingCartItem) => i.type === CART_ITEM_TYPE_FEATURE
      )
    );
  }

  checkCartFeature(feature: FeatureType | ShoppingCartItem): ShoppingCartItem | undefined {
    return (
      this.shopping_cart &&
      this.shopping_cart.shopping_cart_items.find((i: ShoppingCartItem) => i.id == feature.id)
    );
  }

  sanitizedHTML(description: string | undefined): boolean {
    return !stringOnlyHTML(description) && description !== '';
  }

  selectFeature(feature: FeatureType): void {
    this.addToCart(feature);
  }

  openDetailsModal(f: FeatureType, body: string): void {
    let active_element: HTMLElement = document.activeElement as HTMLElement;
    active_element.blur();

    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      modalDialogClass: 'scrollable-modal-maxheight'
    });
    modalRef.componentInstance.modal_title = f.name_override ? f.name_override : f.name;
    modalRef.componentInstance.modal_body = body;
  }

  getAddOnFeatures(): void {
    const param: { [key: string]: number | string | boolean } = {
      planId: this.feature_state.planId!,
      lineId: this.feature_state.lineId!
    };

    if (this.feature_state.include_admin_options != undefined)
      param['include_admin_options'] = this.feature_state.include_admin_options;

    this.subscriptions.add(
      this.shopping_svc
        .getFeatures(this.feature_state.carrierId, param)
        .pipe(
          takeUntil(this.customize_svc.state.plans_param as Observable<BusinessPlansParam>),
          takeUntil(this.customize_svc.state.features_param as Observable<AddOnFeaturesParam>)
        )
        .subscribe({
          next: (res: ManageAddOnFeatures) => {
            this.activity_svc.state().setSteps(res.progress_bar_config?.steps);
            this.customize_svc.state.extras?.next({
              ...this.customize_svc.state.extras?.value,
              number_of_features: res.features.length
            });
            this.features = res.features.sort((a, b) => (a.included > b.included ? -1 : 1));
            this.no_features = res.features.length === 0;

            this.resetCartFeatures();
          },
          error: (err: HttpErrorResponse) => {}
        })
    );
  }

  private resetCartFeatures(): void {
    if (this.cart_features?.length)
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items?.reduce(
        (acc: ShoppingCartItem[], curr: ShoppingCartItem) =>
          curr.type === CART_ITEM_TYPE_FEATURE &&
          (!this.getFeature(curr) ||
            (curr.additional_info?.included && !this.getFeature(curr)?.included))
            ? acc
            : [...acc, curr],
        []
      );

    [...this.features].forEach((f: FeatureType | ShoppingCartItem) => {
      if ((f as FeatureType).included && !this.checkCartFeature(f)) this.addToCart(f);
    });

    this.customize_svc.setCart(this.shopping_cart);
  }

  private addToCart(feature: FeatureType | ShoppingCartItem): void {
    if (this.checkCartFeature(feature))
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
        (i: ShoppingCartItem) => i.id != feature.id
      );
    else
      this.shopping_cart.shopping_cart_items.push(
        createShoppingObject(feature, CART_ITEM_TYPE_FEATURE, this.shopping_cart.carrier?.image_url)
      );

    this.customize_svc.setCart(this.shopping_cart);
  }
}
